import React, { useState, useMemo, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Animated } from 'react-native';
import { IActivity, KeyStage, ConcreteMathConfig, ConcreteItemType } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
    ItemSvgRenderer,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { ArrowRight, RotateCcw } from 'lucide-react-native';

interface ConcreteMathActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

interface ItemInstance {
    id: string;
    index: number;
}

export const ConcreteMathActivity: React.FC<ConcreteMathActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as ConcreteMathConfig;
    const targetCount = config.targetCount ?? 4;
    const initialSourceCount = config.initialSourceCount ?? (targetCount + 2);
    const itemType: ConcreteItemType = config.itemType || 'apple';
    const sourceLabel = config.sourceLabel || (itemType === 'apple' ? 'Tree' : itemType === 'car' ? 'Garage' : 'Start Group');
    const targetLabel = config.targetLabel || (itemType === 'apple' ? 'Basket' : itemType === 'car' ? 'Road' : 'Target Box');

    // Create item instances
    const initialItems: ItemInstance[] = useMemo(() => {
        return Array.from({ length: initialSourceCount }, (_, i) => ({
            id: `item-${i}`,
            index: i,
        }));
    }, [initialSourceCount]);

    const [sourceItems, setSourceItems] = useState<ItemInstance[]>(initialItems);
    const [targetItems, setTargetItems] = useState<ItemInstance[]>([]);
    const [celebrateAnim] = useState(new Animated.Value(1));

    const currentCount = targetItems.length;

    const trafficStatus: TrafficLightStatus = useMemo(() => {
        if (currentCount === 0) return 'red';
        if (currentCount === targetCount) return 'green';
        if (currentCount > targetCount) return 'warning';
        return 'amber';
    }, [currentCount, targetCount]);

    const isComplete = trafficStatus === 'green';

    useEffect(() => {
        if (isComplete) {
            Animated.sequence([
                Animated.timing(celebrateAnim, { toValue: 1.15, duration: 150, useNativeDriver: true }),
                Animated.timing(celebrateAnim, { toValue: 1, duration: 150, useNativeDriver: true }),
            ]).start();
        }
    }, [isComplete, celebrateAnim]);

    const handleTransferToTarget = (item: ItemInstance) => {
        setSourceItems((prev) => prev.filter((it) => it.id !== item.id));
        setTargetItems((prev) => [...prev, item]);
    };

    const handleReturnToSource = (item: ItemInstance) => {
        setTargetItems((prev) => prev.filter((it) => it.id !== item.id));
        setSourceItems((prev) => [...prev, item]);
    };

    const handleReset = () => {
        setSourceItems(initialItems);
        setTargetItems([]);
    };

    const handleSubmit = () => {
        if (!isComplete) return;

        useStrokeStore.getState().setStructuredData({
            placedCount: currentCount,
            targetCount,
            answer: currentCount,
            isCorrect: true,
            inputModeUsed: 'concrete_manipulative',
        });

        onComplete();
    };

    const promptText = config.audioPromptText || activity.instructions || `Move ${targetCount} ${itemType}s into the ${targetLabel.toLowerCase()}!`;

    const itemPlural = itemType === 'apple' ? 'apples' : itemType === 'car' ? 'cars' : itemType === 'star' ? 'stars' : `${itemType}s`;

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
            bounces={false}
        >
            <StageCard style={styles.stageCard}>
                {/* Header with Prompt and Audio Button */}
                <View style={styles.promptHeader}>
                    <View style={styles.promptTextWrap}>
                        <Text style={styles.titleText} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                            {activity.title || `Count ${targetCount} ${itemPlural}`}
                        </Text>
                        <Text style={styles.promptSubText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                            {promptText}
                        </Text>
                    </View>
                    <AudioPromptButton
                        text={promptText}
                    />
                </View>

                {/* Real-Time Traffic Light Indicator */}
                <TrafficLightIndicator
                    status={trafficStatus}
                    current={currentCount}
                    target={targetCount}
                    itemLabel={itemPlural}
                    style={styles.trafficLight}
                />

                {/* Concrete Interactive Stage: Source Container -> Target Container */}
                <View style={styles.stageArena}>
                    {/* Source Container (e.g. Tree / Garage) */}
                    <View style={styles.containerZone}>
                        <View style={styles.zoneHeader}>
                            <Text style={styles.zoneTitle}>{sourceLabel}</Text>
                            <Text style={styles.zoneCountBadge}>{sourceItems.length} left</Text>
                        </View>
                        <View style={styles.itemsGrid}>
                            {sourceItems.map((item) => (
                                <TouchableOpacity
                                    key={item.id}
                                    style={styles.interactiveItem}
                                    onPress={() => handleTransferToTarget(item)}
                                    activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Move ${itemType} to ${targetLabel}`}
                                    accessibilityHint="Tapping moves this item into the target zone"
                                >
                                    <ItemSvgRenderer type={itemType} size={54} />
                                    <View style={styles.tapAddBadge}>
                                        <Text style={styles.tapBadgeText}>+</Text>
                                    </View>
                                </TouchableOpacity>
                            ))}
                            {sourceItems.length === 0 && (
                                <Text style={styles.emptyZoneText}>All {itemPlural} moved!</Text>
                            )}
                        </View>
                    </View>

                    {/* Middle Transfer Directional Indicator */}
                    <View style={styles.transferArrowWrap}>
                        <ArrowRight size={32} color="#0D9488" strokeWidth={3} />
                        <Text style={styles.arrowText}>TAP TO MOVE</Text>
                    </View>

                    {/* Target Container (e.g. Basket / Parking Bay) */}
                    <Animated.View
                        style={[
                            styles.containerZone,
                            styles.targetZone,
                            isComplete && styles.targetZoneComplete,
                            { transform: [{ scale: celebrateAnim }] },
                        ]}
                    >
                        <View style={styles.zoneHeader}>
                            <Text style={[styles.zoneTitle, isComplete && styles.zoneTitleComplete]}>
                                {targetLabel}
                            </Text>
                            <View style={[styles.targetCountPill, isComplete && styles.targetCountPillComplete]}>
                                <Text style={[styles.targetCountText, isComplete && styles.targetCountTextComplete]}>
                                    {currentCount} / {targetCount}
                                </Text>
                            </View>
                        </View>

                        <View style={styles.itemsGrid}>
                            {targetItems.map((item) => (
                                <TouchableOpacity
                                    key={item.id}
                                    style={styles.interactiveItem}
                                    onPress={() => handleReturnToSource(item)}
                                    activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Take ${itemType} out of ${targetLabel}`}
                                    accessibilityHint="Tapping returns this item to the start"
                                >
                                    <ItemSvgRenderer type={itemType} size={54} />
                                    <View style={styles.tapRemoveBadge}>
                                        <Text style={styles.tapBadgeText}>−</Text>
                                    </View>
                                </TouchableOpacity>
                            ))}
                            {targetItems.length === 0 && (
                                <View style={styles.targetEmptyPlaceholder}>
                                    <Text style={styles.targetPlaceholderText}>
                                        Tap {itemPlural} on the left to fill the {targetLabel.toLowerCase()}!
                                    </Text>
                                </View>
                            )}
                        </View>
                    </Animated.View>
                </View>

                {/* C-P-A Bridging Bar: Pictorial tokens & Equation representation */}
                <View style={styles.cpaBridgeBar}>
                    <Text style={styles.cpaLabel}>Your Count:</Text>
                    <View style={styles.cpaTokensRow}>
                        {Array.from({ length: currentCount }).map((_, idx) => (
                            <ItemSvgRenderer key={idx} type={itemType} size={28} />
                        ))}
                    </View>
                    <Text style={styles.cpaEquals}>=</Text>
                    <View style={[styles.cpaNumberBadge, isComplete && styles.cpaNumberBadgeComplete]}>
                        <Text style={[styles.cpaNumberText, isComplete && styles.cpaNumberTextComplete]}>
                            {currentCount}
                        </Text>
                    </View>
                </View>

                {/* Footer Controls: Reset & Hero "GO!" Button */}
                <View style={styles.footerRow}>
                    <TouchableOpacity
                        style={styles.resetButton}
                        onPress={handleReset}
                        activeOpacity={0.7}
                        accessibilityRole="button"
                        accessibilityLabel="Reset all items"
                    >
                        <RotateCcw size={20} color="#64748B" />
                        <Text style={styles.resetButtonText}>Reset</Text>
                    </TouchableOpacity>

                    <JuicyButton
                        label={isComplete ? 'GO! 🚀' : `Put ${targetCount} in ${targetLabel}`}
                        variant="primary"
                        size="large"
                        disabled={!isComplete}
                        onPress={handleSubmit}
                        style={styles.heroGoButton}
                        testID="concrete-math-go-btn"
                    />
                </View>
            </StageCard>
        </ScrollView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: '100%',
        minHeight: 0,
    },
    scrollContent: {
        paddingHorizontal: 16,
        paddingBottom: 40,
        alignItems: 'center',
    },
    stageCard: {
        width: '100%',
        maxWidth: 780,
        padding: 24,
        alignItems: 'stretch',
    },
    promptHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#F0FDFA',
        padding: 16,
        borderRadius: 20,
        borderWidth: 1.5,
        borderColor: '#CCFBF1',
        marginBottom: 16,
        gap: 12,
    },
    promptTextWrap: {
        flex: 1,
    },
    titleText: {
        fontSize: 22,
        fontWeight: '900',
        color: '#0F766E',
        marginBottom: 4,
    },
    promptSubText: {
        fontSize: 16,
        fontWeight: '700',
        color: '#134E4A',
    },
    trafficLight: {
        marginBottom: 16,
    },
    stageArena: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        marginBottom: 20,
    },
    containerZone: {
        flex: 1,
        minHeight: 220,
        backgroundColor: '#F8FAFC',
        borderRadius: 24,
        borderWidth: 2,
        borderColor: '#E2E8F0',
        padding: 14,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 1,
    },
    targetZone: {
        backgroundColor: '#F0FDF4',
        borderColor: '#BBF7D0',
        borderStyle: 'dashed',
        borderWidth: 2.5,
    },
    targetZoneComplete: {
        backgroundColor: '#DCFCE7',
        borderColor: '#22C55E',
        borderStyle: 'solid',
        shadowColor: '#22C55E',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25,
        shadowRadius: 8,
        elevation: 4,
    },
    zoneHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(0,0,0,0.06)',
        paddingBottom: 8,
        marginBottom: 12,
    },
    zoneTitle: {
        fontSize: 16,
        fontWeight: '900',
        color: '#475569',
    },
    zoneTitleComplete: {
        color: '#15803D',
    },
    zoneCountBadge: {
        fontSize: 13,
        fontWeight: '800',
        color: '#64748B',
        backgroundColor: '#E2E8F0',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 12,
    },
    targetCountPill: {
        backgroundColor: '#DCFCE7',
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#86EFAC',
    },
    targetCountPillComplete: {
        backgroundColor: '#22C55E',
        borderColor: '#16A34A',
    },
    targetCountText: {
        fontSize: 14,
        fontWeight: '900',
        color: '#15803D',
    },
    targetCountTextComplete: {
        color: '#FFFFFF',
    },
    itemsGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 140,
    },
    interactiveItem: {
        position: 'relative',
        padding: 4,
        borderRadius: 16,
        backgroundColor: '#FFFFFF',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 3,
        elevation: 2,
    },
    tapAddBadge: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: '#0D9488',
        width: 20,
        height: 20,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
    },
    tapRemoveBadge: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: '#EF4444',
        width: 20,
        height: 20,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
    },
    tapBadgeText: {
        color: '#FFFFFF',
        fontSize: 12,
        fontWeight: '900',
        lineHeight: 14,
    },
    emptyZoneText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#94A3B8',
        fontStyle: 'italic',
    },
    targetEmptyPlaceholder: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
    },
    targetPlaceholderText: {
        fontSize: 14,
        fontWeight: '700',
        color: '#86EFAC',
        textAlign: 'center',
        lineHeight: 20,
    },
    transferArrowWrap: {
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        paddingHorizontal: 4,
    },
    arrowText: {
        fontSize: 10,
        fontWeight: '900',
        color: '#0D9488',
        letterSpacing: 0.5,
    },
    cpaBridgeBar: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFC',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 18,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        marginBottom: 20,
        gap: 10,
    },
    cpaLabel: {
        fontSize: 14,
        fontWeight: '800',
        color: '#475569',
    },
    cpaTokensRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 6,
        flex: 1,
        alignItems: 'center',
    },
    cpaEquals: {
        fontSize: 22,
        fontWeight: '900',
        color: '#1E293B',
    },
    cpaNumberBadge: {
        width: 44,
        height: 44,
        borderRadius: 12,
        backgroundColor: '#FFFFFF',
        borderWidth: 2,
        borderColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cpaNumberBadgeComplete: {
        borderColor: '#22C55E',
        backgroundColor: '#DCFCE7',
    },
    cpaNumberText: {
        fontSize: 22,
        fontWeight: '900',
        color: '#1E293B',
    },
    cpaNumberTextComplete: {
        color: '#15803D',
    },
    footerRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 14,
    },
    resetButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        height: 60,
        paddingHorizontal: 18,
        borderRadius: 20,
        backgroundColor: '#F1F5F9',
        borderWidth: 1.5,
        borderColor: '#CBD5E1',
    },
    resetButtonText: {
        fontSize: 16,
        fontWeight: '800',
        color: '#64748B',
    },
    heroGoButton: {
        flex: 1,
    },
});
